import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ThemeTileContainer } from '../theme/ThemeTile';
import { H3, Button, colors, MagicIcon, SkubotSpinner, Box, H2, ConnectedPlusIcon, Tooltip } from '@commonsku/styles';
import { InstantSearch, Configure } from 'react-instantsearch';
import aa from 'search-insights';
import CustomHits from './CustomHits';
import ProductSuggestionSearch from './ProductSuggestionSearch';
import { connect } from 'react-redux';
import { createLoadDivision, createLoadSupplier } from '../../actions/supplier';
import { getConnectedPlusDivisionOptions } from '../../selectors/dropdowns';
import { isEmpty } from 'lodash';
import { searchClient, indexName, hasAlgoliaConfig } from './Algoliasearch';
import { useSize } from '../../hooks/useSize';
import { calculateContentWidth } from './helpers';
import { trackEvent } from '../../utils/analytics';
import { useIdentity } from '../../hooks';
import { buildDivisionContext } from './algoliaDivisionUtils';
import { makeProductSearchDispatchers } from '../product-search/shared/addItemDispatcher';
import { useFullOrder } from '../product-search/shared/useFullOrder';

const itemsPerPage = 10;
const MAX_SEARCH_TERMS = 3;

const ScrollableCollectionsContainer = styled.div`
  width: 100%;
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const SuggestionView = ({ themes, order, full_order, onSelectTheme, loading, onAddItem, onDeleteItem, divisionsArray, setIsSuggestSearch, setSuggestion, suggestions, suggestionsHitLength, setSuggestionsHitLength }) => {
  const [ref, size] = useSize();
  const contentWidth = calculateContentWidth(size.clientWidth ?? 0);
  const identity = useIdentity();

  const renderProductSuggestionSection = (searchTerm, index, title) => {
    function onViewMoreClick() {
      setSuggestion(searchTerm);
      setIsSuggestSearch(true);
      trackEvent('click_llm_suggestion_viewmore', {
        tenant_id: identity.tenant_id,
        login_email: identity.login_email,
        title: title
      });
    }
    return (
      suggestionsHitLength[index] !== 0 &&
      <div key={`suggestion-${index}`} style={{ display: 'flex', flexDirection: 'column', gap: 8, width: contentWidth }}>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Tooltip
              id={`suggestion-tooltip-${index}`}
              place='bottom-start'
              style={{ backgroundColor: "rgba(18, 57, 82, 0.9)", padding: '13px 16px 13px 16px', borderRadius: 5, fontSize: 16, zIndex: 1 }}
              arrowColor='rgba(18, 57, 82, 0.9)'
            />
            <MagicIcon altText='' color={colors.neutrals.darkest} size='large' data-tooltip-id={`suggestion-tooltip-${index}`} data-tooltip-content={'Smart picks based on your order history'} />
            <H3 style={{ fontWeight: 700, fontSize: 20, lineHeight: '32px', marginBottom: 0, color: colors.neutrals.darkest }}>{title}</H3>
          </div>
          <Button
            variantBg='transparent'
            variantBorderColor='transparent'
            variantColor={colors.primary1.main}
            variantHoverBorderColor={colors.primary1['20']}
            variantHoverColor={colors.primary1.main}
            variantHoverBg={colors.primary1['20']}
            size="medium" variant='custom' onClick={() => {
              onViewMoreClick();
            }}>View More</Button>
        </div>
        <InstantSearch searchClient={searchClient} indexName={indexName} future={{ preserveSharedStateOnUnmount: true }} insights={true} >
          <CustomHits loading={loading} order={order} full_order={full_order} onAddItem={onAddItem} onDeleteItem={onDeleteItem} divisions={divisionsArray} index={index} setHitLength={setSuggestionsHitLength} />
          <Configure userToken={identity.tenant_id} hitsPerPage={itemsPerPage} clickAnalytics={true} query={searchTerm} filters="is_converted:true" />
        </InstantSearch >
      </div>
    );
  };

  const renderSuggestionSections = () => {
    if (!suggestions || !suggestions.length) {
      return null;
    }

    const termsToShow = suggestions.slice(0, MAX_SEARCH_TERMS);
    return termsToShow.map((suggestion, index) => renderProductSuggestionSection(suggestion.searchTerm, index, `${suggestion.title} - ${suggestion.searchTerm}`));
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 32,
      overflowY: 'auto',
      overflowX: 'hidden',
      marginTop: 20,
      backgroundImage: `linear-gradient(${colors.primary1.light}, transparent 469px)`,
      alignItems: 'center',
      padding: '30px 70px',
      borderRadius: 5
    }} ref={ref}>
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        width: contentWidth,
        justifyContent: 'center'
      }}>
        <ConnectedPlusIcon color={colors.neutrals.darkest} size='large' />
        <H2 mb={0} color={colors.neutrals.darkest}>Connected+ AI Recommendations</H2>
      </div>
      {renderSuggestionSections()}
      {!isEmpty(themes) && (
        <div style={{ width: contentWidth }}>
          <ScrollableCollectionsContainer>
            <ThemeTileContainer themes={themes} order={order} title={'Featured Collections'} onClick={onSelectTheme} styles={{ width: contentWidth }} />
          </ScrollableCollectionsContainer>
        </div>
      )}
    </div>
  );
};

export function SuggestionContainer({ themes, order, onSelectTheme, isSuggestSearch, loading, onAddItem, onDeleteItem, divisions, loadDivisions, onClear, setIsSuggestSearch, suggestions, recommendationsLoading, tenantId, isProductsPage }) {
  const userToken = tenantId || order?.tenant_id;
  const [selectedSuggestion, setSelectedSuggestion] = useState('');
  const [suggestionsHitLength, setSuggestionsHitLength] = useState(Array(MAX_SEARCH_TERMS).fill(null));
  const full_order = useFullOrder(order);
  const isAllSuggestionsEmpty = useMemo(() => {
    const data = suggestionsHitLength;
    return data.every(length => {
      return length === 0;
    });
  }, [suggestionsHitLength]);

  // Load divisions once on mount
  useEffect(() => {
    if (order?.order_type === 'COLLECTION') {
      return;
    }
    loadDivisions('connected-plus');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userToken) {
      aa('setUserToken', userToken);
    }
  }, [userToken]);

  const divisionsArray = useMemo(() => {
    return buildDivisionContext(divisions);
  }, [divisions]);

  if (recommendationsLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32, marginTop: 20, justifyContent: 'center', alignItems: 'center' }}>
        <SkubotSpinner />
        <H3 style={{ fontWeight: 700, fontSize: 20, lineHeight: '32px', marginBottom: 0, color: colors.neutrals.darkest }}>Generating recommendations…</H3>
      </div>
    );
  }

  if (isSuggestSearch || isProductsPage) {
    return (
      <ProductSuggestionSearch
        order={order}
        full_order={full_order}
        loading={loading}
        onAddItem={onAddItem}
        onDeleteItem={onDeleteItem}
        divisions={divisionsArray}
        onClear={onClear}
        suggestion={selectedSuggestion}
        setSuggestion={setSelectedSuggestion}
        tenantId={userToken}
        isProductsPage={isProductsPage}
      />
    );
  }

  if (isEmpty(suggestions) || !hasAlgoliaConfig || isAllSuggestionsEmpty) {
    return (
      <Box borderless style={{
        padding: '20px', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', marginTop: 20
      }}>
        <ScrollableCollectionsContainer>
          <ThemeTileContainer themes={themes} order={order} title={'Featured Collections'} onClick={onSelectTheme} />
        </ScrollableCollectionsContainer>
      </Box>
    );
  }

  return (
    <SuggestionView
      themes={themes}
      order={order}
      full_order={full_order}
      onSelectTheme={onSelectTheme}
      loading={loading}
      onAddItem={onAddItem}
      onDeleteItem={onDeleteItem}
      divisionsArray={divisionsArray}
      setIsSuggestSearch={setIsSuggestSearch}
      suggestions={suggestions}
      setSuggestion={setSelectedSuggestion}
      suggestionsHitLength={suggestionsHitLength}
      setSuggestionsHitLength={setSuggestionsHitLength}
    />
  );
}

const mapStateToProps = (state, ownProps) => ({
  loading: state.display.loading.add_item,
  divisions: getConnectedPlusDivisionOptions(state),
});

const mapDispatchToProps = (dispatch, ownProps) => {
  const { order, isAddingToProjectNote, catalogId } = ownProps;
  const { addItem, deleteItem } = makeProductSearchDispatchers({
    dispatch, order, catalogId, isAddingToProjectNote,
  });

  return {
    onAddItem: (item, kwargs = { fetch_update: true }) => addItem({
      parentId: item.objectID,
      copiedFrom: item.copied_from,
      currencyId: item.currency_id,
      sku: item.product_supplier_code,
      divisionId: item?.division_id,
      isSageConnect: 'sage_connect' === item.copied_from,
    }, kwargs),
    onDeleteItem: item_id => deleteItem(item_id),
    loadDivisions: supplier_type => {
      if ('collection' === supplier_type) {
        dispatch(createLoadSupplier(supplier_type));
      } else {
        supplier_type = 'sage-connect' === supplier_type ? 'sage' : supplier_type;
        dispatch(createLoadDivision('commonsku' === supplier_type ? 'supplier-direct' : supplier_type));
      }
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SuggestionContainer);
